import {
  AreaCategory,
  buildDayForBooking,
  CardioOrResistance,
  IUserRecommendedSettings,
  selectRecommendedSettingsBasedOnArea,
  WorkoutSettings,
} from 'fitbeat.gym-core';
import moment from 'moment-timezone';
import React, { Component, FC } from 'react';
import { translate } from 'react-i18next';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { connect } from 'react-redux';
import { Button, ButtonSize, ButtonTypes } from '../../components';
import { IAppStore } from '../../HomeWorkouts';
import theme from '../../theme';
import { UserDataConsumer } from '../../utils';
import { Logger } from '../../utils/Logger';
import { defaultScale } from '../../utils/scaling';
import { mappedTitles } from '../../WorkoutGoalAssistant';
import { mappedWorkoutType } from './WorkoutTypeSelection';

interface IOwnStateProps {
  loadingRecommendedSettings: boolean;
  recommendedSettings: WorkoutSettings | null;
}
interface IMapStateProps {
  currentSelectedGymName: string | undefined;
  currentArea: AreaCategory | undefined;
}

interface IOwnProps {
  isWorkoutTypeChangeLoading: boolean;
  workoutToChangeTo: CardioOrResistance;
  Heading: React.ComponentType;
  textColor: TextStyle;
  containerStyle?: ViewStyle;
  fetchRecommendedSettingsOnMount?: boolean;
  saveAndClose(): void;
  onBackPress(): void;
}

interface IUserProps {
  user: any;
  t(str: string, args?: any): string;
}

type Props = IMapStateProps & IOwnProps & IUserProps;

class TypeOfWorkoutWarningComponent extends Component<Props, IOwnStateProps> {
  constructor(props) {
    super(props);
    this.state = {
      recommendedSettings: null,
      loadingRecommendedSettings: true,
    };
  }

  public async componentDidMount(): Promise<void> {
    const {
      user: { workoutSessionService, userProfile },
      currentSelectedGymName,
      currentArea,
    } = this.props;

    const day = buildDayForBooking(moment(), /* isToday */ true);
    try {
      const allSettings: IUserRecommendedSettings = await workoutSessionService.getRecommendedSettingsForDate(
        day,
        userProfile.longTermGoal,
        currentSelectedGymName,
      );
      const recommendedSettings = selectRecommendedSettingsBasedOnArea(allSettings, currentArea);
      this.setState({ recommendedSettings });
    } catch (e) {
      Logger.debug(
        `Failed to fetch recommended settings for type of workout change: ${e}`,
      );
    } finally {
      this.setState({ loadingRecommendedSettings: false });
    }
  }

  public render() {
    const {
      t,
      workoutToChangeTo,
      onBackPress,
      saveAndClose,
      isWorkoutTypeChangeLoading,
      Heading,
      containerStyle,
      textColor,
    } = this.props;

    const { loadingRecommendedSettings } = this.state;
    const currentworkoutDescription = `${mappedWorkoutType[workoutToChangeTo].title} `
      + `(${mappedWorkoutType[workoutToChangeTo].parentheticDescription})`;
    if (loadingRecommendedSettings) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator
            size='small'
            color={theme.PRIMARY}
            animating
          />
        </View>
      );
    }

    return (
      <>
        <Heading />
        <View style={[styles.container, containerStyle]}>
          <View style={styles.textContainer}>
            <Text style={[styles.heading, textColor]}>
              {t('typeOfWorkoutWarningHeader', {
                workoutDescription: currentworkoutDescription,
              })}
            </Text>
            {this.renderWarningText()}
          </View>
          <Button
            title={t('typeOfWorkoutWarningConfrim', {
              burnOrBuild: mappedWorkoutType[workoutToChangeTo].title,
            })}
            isLoading={isWorkoutTypeChangeLoading}
            disabled={isWorkoutTypeChangeLoading}
            onPress={saveAndClose}
            fontColor={theme.WHITE}
            containerStyle={styles.button}
            size={ButtonSize.MEDIUM}
          />
          <Button
            size={ButtonSize.MEDIUM}
            title={t('typeOfWorkoutWarningReject')}
            onPress={onBackPress}
            fontColor={theme.PRIMARY}
            buttonType={ButtonTypes.OUTLINE}
            containerStyle={styles.button}
          />
        </View>
      </>
    );
  }

  private renderWarningText() {
    const { recommendedSettings } = this.state;
    const {
      workoutToChangeTo,
      user: { userProfile },
      t,
      textColor,
    } = this.props;
    const showWarningText = recommendedSettings
      && recommendedSettings?.cardioOrResistance !== workoutToChangeTo;
    if (!showWarningText) {
      return null;
    }

    const goal = mappedTitles[userProfile.longTermGoal];
    const nextworkoutDescription = `${mappedWorkoutType[recommendedSettings.cardioOrResistance].title} `
      + `(${
        mappedWorkoutType[recommendedSettings.cardioOrResistance]
          .parentheticDescription
      })`;
    return (
      <Text style={[styles.description, textColor]}>
        {t('typeOfWorkoutWarningDescription_1')}
        <Text style={[styles.boldDescription, textColor]}>{goal}</Text>
        {t('typeOfWorkoutWarningDescription_3')}
        <Text style={[styles.boldDescription, textColor]}>
          {t('typeOfWorkoutWarningDescription_4', {
            workoutDescription: nextworkoutDescription,
          })}
        </Text>
        {t('typeOfWorkoutWarningDescription_5')}
      </Text>
    );
  }
}

const mapStateToProps = (state: IAppStore): IMapStateProps => {
  const currentSelectedGymName = state.bookings.bookingWizard.currentSelectedGymName
    ?? state.homeWorkouts.homeWorkoutSession?.gym?.name;
  const currentArea = state.bookings.bookingWizard.areaCategory || 'circuit';
  return {
    currentSelectedGymName,
    currentArea,
  };
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: defaultScale(28),
  },
  boldDescription: {
    ...theme.textStyle.bodyCopyBold,
  },
  heading: {
    ...theme.textStyle.heading,
    textAlign: 'center',
    color: theme.WHITE,
    marginBottom: defaultScale(30),
  },
  description: {
    ...theme.textStyle.bodyCopyReg,
    color: theme.WHITE,
    textAlign: 'center',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  button: {
    width: '100%',
    marginBottom: defaultScale(10),
  },
  textContainer: {
    width: '100%',
    marginBottom: defaultScale(30),
  },
});

const TypeOfWorkoutWarning: FC<IOwnProps> = connect<IMapStateProps>(
  mapStateToProps,
  null,
)(
  translate(['bookings'], { wait: true })(
    UserDataConsumer(TypeOfWorkoutWarningComponent),
  ),
);

export default TypeOfWorkoutWarning;
